import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { LogOut, Search, Download, Trash2, RefreshCw, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: "Čeká",
  contacted: "Kontaktován",
  paid: "Zaplaceno",
  redeemed: "Využito",
  cancelled: "Storno",
};
const STATUS_COLORS = {
  pending: "bg-zinc-700/40 text-zinc-200 border-zinc-600",
  contacted: "bg-blue-900/30 text-blue-200 border-blue-800",
  paid: "bg-emerald-900/30 text-emerald-200 border-emerald-800",
  redeemed: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40",
  cancelled: "bg-red-900/30 text-red-200 border-red-900",
};
const VTYPE = { essential: "Essential", prestige: "Prestige", supreme: "Supreme", allin: "All In" };

const fmt = (n) => (n ?? 0).toLocaleString("cs-CZ").replace(/,/g, " ");
const fmtDate = (iso) => new Date(iso).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });

export default function AdminDashboard() {
  const { user, loading, logout, authHeaders } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [status, setStatus] = useState("");
  const [voucherType, setVoucherType] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (voucherType) params.voucher_type = voucherType;
      if (q) params.q = q;
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/reservations`, { params, headers: authHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      ]);
      setReservations(r.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Nepodařilo se načíst data.");
      }
    } finally {
      setBusy(false);
    }
  }, [status, voucherType, q, authHeaders, logout, navigate]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Celkem rezervací", value: fmt(stats.total_reservations) },
      { label: "Zbývá slotů", value: fmt(stats.remaining_slots), accent: true },
      { label: "Zaplaceno (Kč)", value: fmt(stats.total_revenue_paid) },
      { label: "V procesu (Kč)", value: fmt(stats.total_revenue_pending) },
    ];
  }, [stats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500 text-xs tracking-[0.3em] uppercase">
        Načítám…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/reservations/${id}`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      toast.success(`Stav aktualizován: ${STATUS_LABEL[newStatus]}`);
      fetchData();
    } catch {
      toast.error("Nepodařilo se aktualizovat stav.");
    }
  };

  const removeReservation = async (id) => {
    if (!window.confirm("Opravdu smazat tuto rezervaci?")) return;
    try {
      await axios.delete(`${API}/admin/reservations/${id}`, { headers: authHeaders() });
      toast.success("Rezervace smazána.");
      fetchData();
    } catch {
      toast.error("Nepodařilo se smazat.");
    }
  };

  const sendVoucher = async (r) => {
    const ok = window.confirm(
      `Odeslat aktivovaný voucher na ${r.email}?\n\n` +
      `Voucher: ${r.voucher_name} (${r.voucher_code})\n` +
      `Držitel: ${r.name}\n\n` +
      `Stav bude změněn na "Zaplaceno" a klient obdrží finální voucher s QR kódem.`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/reservations/${r.id}/send-voucher`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Voucher ${data.voucher_code} odeslán na ${data.email}.`);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Odeslání voucheru selhalo.");
    }
  };

  const sendPaymentQr = async (r) => {
    const ok = window.confirm(
      `Odeslat QR platbu + účtenku na ${r.email}?\n\n` +
      `Voucher: ${r.voucher_name} (${r.voucher_code})\n` +
      `Částka: ${(r.price ?? "").toLocaleString?.("cs-CZ") ?? r.price} Kč\n\n` +
      `Klient obdrží e-mail s QR kódem pro platbu převodem a účtenku v příloze.`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/reservations/${r.id}/send-payment-qr`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`QR platba pro ${data.voucher_code} odeslána na ${data.email}.`);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Odeslání QR platby selhalo.");
    }
  };

  const redeemVoucher = async (r) => {
    const ok = window.confirm(
      `Označit voucher jako VYUŽITÝ?\n\n` +
      `Voucher: ${r.voucher_name} (${r.voucher_code})\n` +
      `Klient: ${r.name} <${r.email}>\n\n` +
      `Klient obdrží e-mail se záručním listem na sklokeramickou ochranu na 2 roky ` +
      `(platnost ode dneška do +2 roky). Stav se změní na "Využito".`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/reservations/${r.id}/redeem`,
        {},
        { headers: authHeaders() }
      );
      toast.success(
        `Voucher ${data.voucher_code} označen jako využitý. ` +
        (data.has_warranty
          ? `Záruční list odeslán na ${data.email}.`
          : `E-mail o využití odeslán na ${data.email}.`)
      );
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Označení voucheru selhalo.");
    }
  };

  const exportCsv = () => {
    if (!reservations.length) {
      toast.error("Žádná data k exportu.");
      return;
    }
    const rows = [
      ["ID", "Kód", "Jméno", "E-mail", "Telefon", "Voucher", "Cena", "Hodnota", "Stav", "Poznámka", "Datum"],
      ...reservations.map((r) => [
        r.id,
        r.voucher_code || "",
        r.name,
        r.email,
        r.phone,
        r.voucher_name || VTYPE[r.voucher_type] || r.voucher_type,
        r.price,
        r.value,
        STATUS_LABEL[r.status] || r.status,
        (r.note || "").replace(/\n/g, " "),
        r.created_at,
      ]),
    ];
    const csv = rows
      .map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `carspa_reservations_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative">
      <div className="grain-overlay" aria-hidden="true" />

      {/* Header */}
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#D4AF37]/50 flex items-center justify-center">
              <span className="font-display text-[#D4AF37] text-xl leading-none">C</span>
            </div>
            <div className="leading-none">
              <div className="font-display text-xl tracking-wide text-white">CarSpa</div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37]/80">Admin Dashboard</div>
            </div>
          </a>
          <div className="flex items-center gap-6">
            <span className="text-xs text-zinc-400 hidden md:inline" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-zinc-300 hover:text-[#D4AF37] transition-colors"
            >
              <LogOut size={14} strokeWidth={1.2} /> Odhlásit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-10">
        {/* Stat cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-white/5 border border-white/5">
          {statCards.map((s) => (
            <div
              key={s.label}
              className={`bg-[#060606] p-6 md:p-8 ${s.accent ? "" : ""}`}
              data-testid={`admin-stat-${s.label}`}
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                {s.label}
              </div>
              <div className={`font-display text-4xl ${s.accent ? "text-gold-gradient" : "text-white"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </section>

        {/* Breakdown */}
        {stats && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="gold-border p-6">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Podle stavu</div>
              <div className="space-y-2">
                {Object.entries(stats.by_status).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-2 text-sm">
                    <span className="text-zinc-300">{STATUS_LABEL[k] || k}</span>
                    <span className="text-white font-medium tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="gold-border p-6">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Podle voucheru</div>
              <div className="space-y-2">
                {Object.entries(stats.by_voucher).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-2 text-sm">
                    <span className="text-zinc-300">{VTYPE[k] || k}</span>
                    <span className="text-white font-medium tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Filters */}
        <section className="flex flex-col md:flex-row md:items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-[240px] border-b border-white/10 focus-within:border-[#D4AF37] transition-colors">
            <Search size={16} className="text-zinc-500" strokeWidth={1.2} />
            <input
              data-testid="admin-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Hledat jméno, e-mail, telefon…"
              className="flex-1 bg-transparent py-3 text-white placeholder:text-zinc-600 focus:outline-none text-sm"
            />
          </div>
          <select
            data-testid="admin-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-transparent border border-white/10 py-3 px-4 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="" className="bg-black">Všechny stavy</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k} className="bg-black">{v}</option>
            ))}
          </select>
          <select
            data-testid="admin-filter-voucher"
            value={voucherType}
            onChange={(e) => setVoucherType(e.target.value)}
            className="bg-transparent border border-white/10 py-3 px-4 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="" className="bg-black">Všechny vouchery</option>
            {Object.entries(VTYPE).map(([k, v]) => (
              <option key={k} value={k} className="bg-black">{v}</option>
            ))}
          </select>
          <button
            onClick={fetchData}
            disabled={busy}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-zinc-300 hover:text-[#D4AF37] border border-white/10 px-4 py-3 transition-colors"
          >
            <RefreshCw size={14} strokeWidth={1.2} className={busy ? "animate-spin" : ""} /> Obnovit
          </button>
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-black bg-[#D4AF37] hover:bg-[#F0C846] px-4 py-3 transition-colors font-semibold"
          >
            <Download size={14} strokeWidth={1.5} /> Export CSV
          </button>
        </section>

        {/* Reservations table */}
        <section data-testid="admin-reservations-table" className="gold-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/60 border-b border-white/5">
              <tr className="text-left text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                <th className="p-4">Klient</th>
                <th className="p-4">Kontakt</th>
                <th className="p-4">Voucher</th>
                <th className="p-4">Stav</th>
                <th className="p-4">Datum</th>
                <th className="p-4 text-right">Akce</th>
              </tr>
            </thead>
            <tbody>
              {reservations.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-zinc-500">
                    Žádné rezervace neodpovídají filtru.
                  </td>
                </tr>
              )}
              {reservations.map((r) => (
                <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4">
                    <div className="text-white">{r.name}</div>
                    {r.note && (
                      <div className="text-xs text-zinc-500 mt-1 max-w-xs truncate" title={r.note}>
                        {r.note}
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="text-zinc-300">{r.email}</div>
                    <div className="text-xs text-zinc-500">{r.phone}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-[#D4AF37]">{r.voucher_name || VTYPE[r.voucher_type]}</div>
                    <div className="text-xs text-zinc-500">{fmt(r.price)} Kč · hodnota {fmt(r.value)} Kč</div>
                    {r.voucher_code && (
                      <div className="text-[10px] font-mono tracking-wider text-zinc-400 mt-1" title="Kód voucheru">
                        {r.voucher_code}
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <select
                      data-testid={`admin-status-select-${r.id}`}
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`bg-transparent border px-3 py-1.5 text-xs tracking-[0.2em] uppercase ${STATUS_COLORS[r.status] || "border-white/10 text-zinc-300"}`}
                    >
                      {Object.entries(STATUS_LABEL).map(([k, v]) => (
                        <option key={k} value={k} className="bg-black">{v}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-4 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {r.voucher_sent_at ? (
                        <span
                          data-testid={`admin-voucher-sent-${r.id}`}
                          title={`Voucher odeslán ${fmtDate(r.voucher_sent_at)}`}
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-emerald-400 border border-emerald-800 bg-emerald-500/10 px-2 py-1"
                        >
                          <CheckCircle2 size={12} strokeWidth={1.5} /> Odeslán
                        </span>
                      ) : (
                        <button
                          onClick={() => sendVoucher(r)}
                          data-testid={`admin-send-voucher-${r.id}`}
                          title="Vygenerovat a odeslat aktivovaný voucher"
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-black bg-[#D4AF37] hover:bg-[#F0C846] px-3 py-1.5 transition-colors font-semibold"
                        >
                          <Send size={12} strokeWidth={1.8} /> Odeslat voucher
                        </button>
                      )}
                      <button
                        onClick={() => sendPaymentQr(r)}
                        data-testid={`admin-send-payment-qr-${r.id}`}
                        title="Odeslat QR platbu + účtenku klientovi"
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white border border-[#D4AF37]/60 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-1.5 transition-colors font-semibold"
                      >
                        <Send size={12} strokeWidth={1.8} /> QR Platba
                      </button>
                      {r.status === "paid" && (
                        <button
                          onClick={() => redeemVoucher(r)}
                          data-testid={`admin-redeem-${r.id}`}
                          title="Označit voucher jako využitý a odeslat záruční list na sklokeramiku (2 roky)"
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 transition-colors font-semibold"
                        >
                          ✓ Využito
                        </button>
                      )}
                      {r.status === "redeemed" && (
                        <span
                          data-testid={`admin-redeemed-badge-${r.id}`}
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] border border-[#D4AF37]/50 px-3 py-1.5 font-semibold"
                          title={r.redeemed_at ? `Využito ${new Date(r.redeemed_at).toLocaleDateString('cs-CZ')}` : ""}
                        >
                          ✓ Využito
                        </span>
                      )}
                      <button
                        onClick={() => removeReservation(r.id)}
                        data-testid={`admin-delete-${r.id}`}
                        className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                        aria-label="Smazat rezervaci"
                      >
                        <Trash2 size={16} strokeWidth={1.3} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
